import { Input, Selector } from '@casumo/dojo-react';
import { ChevronDown, Search } from '@casumo/dojo-react-icons';
import { getName } from 'i18n-iso-countries';
import { ChangeEvent, FC, memo, useEffect, useMemo, useState } from 'react';
import useCommonTranslations from '../../api/hooks/useCommonTranslations.tsx';
import useCountriesData from '../../api/hooks/useCountriesData.tsx';
import Drawer from '../../design-system/drawer.tsx';
import Skeleton from '../../design-system/skeleton.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TPhoneNumberInputField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import { filterAndSortCountries } from '../../utils/utils.ts';

const InputDialog: FC<{
  dialogTitle: string;
}> = memo(({ dialogTitle }) => {
  const { countries, isLoading: countriesDataIsLoading, error: countriesDataError } = useCountriesData();
  const {
    commonTranslations,
    isLoading: commonTranslationsIsLoading,
  } = useCommonTranslations();

  const {
    formData,
    metadata,
    setFormData,
    setMetadata,
  } = useRegistrationStore();

  const countryNames = useMemo(() => countries.map((country) => ({
    name: country.name,
    flag: country.flag,
    code: country.callingCode,
  })).sort((a, b) => a.name.localeCompare(b.name)), [countries]);

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleIsOpen = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedCountry = JSON.parse(e.currentTarget.value);
    setFormData({ phonePrefix: selectedCountry.code });
    setMetadata({ phonePrefixSelectedCountry: selectedCountry });
    setIsOpen(false);
  };

  const filteredCountries = useMemo(
    () => filterAndSortCountries(countryNames, searchValue),
    [countryNames, searchValue],
  );

  useEffect(() => {
    if (countries.length) {
      const prefix = formData?.phonePrefix || countries
        .find(country =>
          country.name === getName(formData?.countryOfResidence, 'en'))
        ?.callingCode;

      setFormData({ phonePrefix: prefix });

      const matchingCountry = countryNames?.find(c => c.code === prefix);
      if (matchingCountry && matchingCountry.name !== metadata?.phonePrefixSelectedCountry?.name) {
        setMetadata({ phonePrefixSelectedCountry: matchingCountry });
      }
    }
  }, [
    countries,
    countryNames,
    formData?.countryOfResidence,
    formData?.phonePrefix,
    metadata?.phonePrefixSelectedCountry?.name,
    setFormData,
    setMetadata,
  ]);

  if (countriesDataIsLoading || countriesDataError || commonTranslationsIsLoading) {
    return <Skeleton height="h-[18px] w-[25px]" />;
  }

  return (
    <>
      <div className="flex items-center gap-2xs cursor-pointer text-black "
        onClick={() => handleIsOpen(true)}
        data-testid={TEST_ID.COMBOBOX_TRIGGER + 'phonePrefix'}
      >
        <img
          className="h-md w-md"
          src={
            countries.find(country =>
              country.callingCode === formData?.phonePrefix
              || country.name === getName(formData?.countryOfResidence, 'en'),
            )?.flag
          }
          alt={formData?.phonePrefix || ''}
        />
        {formData?.phonePrefix || countries
          .find(country =>
            country.name === getName(formData?.countryOfResidence, 'en'),
          )?.callingCode
        }
        <span className="text-[24px] text-grey-50">
          <ChevronDown />
        </span>
      </div>
      <Drawer isOpen={isOpen} setIsOpen={handleIsOpen}>
        <div className="flex flex-col h-full w-full p-md overflow-hidden">
          <div className="flex justify-between text-white pb-sm font-bold">
            <h1 className="text-h-lg">{dialogTitle}</h1>
            <span className="p-2xs cursor-pointer" onClick={() => handleIsOpen(false)}>X</span>
          </div>
          <Input
            id="countrySearch"
            name="search"
            placeholder={commonTranslations?.search ?? 'Search'}
            variant="dark"
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            value={searchValue}
            prefix={<span className="text-[24px]"><Search /></span>}
            clearable
            autoFocus
            onClear={() => setSearchValue('')}
            data-testid={TEST_ID.SEARCH + 'phonePrefix'}
          />
          <Selector name="code" variant="dark" direction="vertical"
            className="flex-1 w-full mt-md px-0.5 overflow-y-auto">
            {filteredCountries.map((country, idx) =>
              <Selector.Radio key={idx} value={JSON.stringify(country)} onChange={handleCodeChange}
                className="flex gap-2xs"
                data-testid={TEST_ID.PHONE_PREFIX + country.name}
              >
                <img className="h-md w-md"
                  src={country.flag} alt={country.name} />{country.name} ({country.code})
              </Selector.Radio>,
            )}
          </Selector>
        </div>
      </Drawer>
    </>
  );
});
InputDialog.displayName = 'InputDialog';

const PhoneNumberInput: FC<TPhoneNumberInputField & {
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onClear: () => void;
  value: string;
  onBlur?: (field: Partial<TPhoneNumberInputField>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}> = memo(({ name, label, value, onChange, dialogTitle, onClear, validation, onBlur, onKeyDown }) => {
  const { errors } = useRegistrationStore();

  return (
    <Input
      id="phoneNumberInput"
      name={name}
      label={label}
      type="tel"
      prefix={
        <InputDialog dialogTitle={dialogTitle} />
      }
      value={value}
      onChange={(e) => {
        if (isNaN(+e.currentTarget.value)) {
          return;
        }
        onChange(e)
      }}
      onClear={onClear}
      error={!!errors[name]}
      data-error={!!errors[name]}
      errorMessage={errors[name]}
      onBlur={() => onBlur?.({ name, validation })}
      onKeyDown={onKeyDown}
      data-testid={TEST_ID.INPUT + name}
    />
  );
});
PhoneNumberInput.displayName = 'PhoneNumberInput';

export { PhoneNumberInput };
